import { type RefObject, useMemo } from "react";
import { type NumberFormatValues } from "react-number-format";
import { type CellContext, type ColumnDef } from "@tanstack/react-table";
import { CloseIcon } from "vega-icons";

import { type Currency } from "@vega-api/generated/types/currency";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ClientAvatar } from "@vega-core/components/ClientAvatar/ClientAvatar";
import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { type BaseMultiSelectOption } from "@vega-core/components/Form/Controls/Dropdown/ThemedMultiselectDropdown/ThemedMultiselectDropdown";
import { TextareaInput } from "@vega-core/components/Form/Controls/Textarea/TextareaInput";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";

import { MoneyInputWithTooltipError } from "../components/MoneyInputWithTooltipError";
import { MultiselectWithTooltipError } from "../components/MultiselectWithTooltipError";
import { TIER_OPTIONS } from "../constants/prospectConstants";
import {
  $selectedProspectsData,
  type ProspectWithFormData,
  type ValidationErrors,
} from "../stores/prospectsStore";

export const useAddNotesForCommitteeColumns = (
  currency: Currency | undefined,
  getValidationErrors: () => Partial<Record<string, ValidationErrors>>,
  tableRef?: RefObject<HTMLDivElement | null>,
  reasonForInclusionOptions: BaseMultiSelectOption[] = [],
) => {
  const handleRemoveProspect = (limitedPartnerId: string) => {
    const currentData = $selectedProspectsData.get();
    const { [limitedPartnerId]: _, ...newData } = currentData;
    $selectedProspectsData.set(newData);
  };
  const columns: ColumnDef<ProspectWithFormData>[] = useMemo(
    () => [
      {
        accessorKey: "limitedPartnerName",
        maxSize: 600,
        header: m.coInvestments_limitedPartner(),
        cell: ({ row }: CellContext<ProspectWithFormData, unknown>) => (
          <span className="body-base-medium">
            {row.original.limitedPartnerName}
          </span>
        ),
      },
      {
        id: "removeProspect",
        header: "",
        isMobileAction: true,
        addCellTooltip: false,
        enableSorting: false,
        cell: ({ row }: CellContext<ProspectWithFormData, unknown>) => (
          <ThemedButton
            ariaLabel={m.coInvestments_prospects_remove_button()}
            className="px-2.5 py-1.5 min-h-5 hover:shadow-sm"
            dataTestId={`remove-prospect-button-${row.index}`}
            icon={<CloseIcon className="text-text/negative size-4" />}
            iconPosition="left"
            onClick={() => handleRemoveProspect(row.original.limitedPartnerId)}
            size="small"
            tooltip={m.coInvestments_prospects_remove_button()}
            variant="secondary"
          />
        ),
      },
      {
        accessorKey: "tierLevel",
        header: m.coInvestments_tier(),
        size: 96,
        addCellTooltip: false,
        cell: ({ row }: CellContext<ProspectWithFormData, unknown>) => {
          const currentData = $selectedProspectsData.get();
          const storeData = currentData[row.original.limitedPartnerId];
          const currentTier = storeData.tierLevel;

          return (
            <ThemedDropdown
              isClearable={false}
              name={`tier-${row.original.limitedPartnerId}`}
              onChange={(option) => {
                $selectedProspectsData.set({
                  ...$selectedProspectsData.get(),
                  [row.original.limitedPartnerId]: {
                    ...row.original,
                    tierLevel: option?.value ?? "1",
                  },
                });
              }}
              options={TIER_OPTIONS}
              renderOption={(option: OptionType) => (
                <ClientAvatar
                  isBold
                  name={option.label}
                  size="small"
                  variant="primaryBackground"
                />
              )}
              value={currentTier}
            />
          );
        },
      },
      {
        accessorKey: "estimatedTicketSizeMin",
        header: m.common_fieldOptional({
          name: m.coInvestments_prospects_estimatedTicketSizeMin(),
        }),
        size: 240,
        maxSize: 240,
        addCellTooltip: false,
        cell: ({ row }: CellContext<ProspectWithFormData, unknown>) => {
          const currentData = $selectedProspectsData.get();
          const storeData = currentData[row.original.limitedPartnerId];
          // Get errors from getter function to avoid stale closure
          const prospectErrors =
            getValidationErrors()[row.original.limitedPartnerId];
          const error = prospectErrors?.estimatedTicketSizeMin;

          return (
            <MoneyInputWithTooltipError
              allowAbbreviations
              currency={currency}
              error={error}
              hideLabelTooltip
              name={`estimatedTicketSizeMin-${row.original.limitedPartnerId}`}
              onValueChange={(values: NumberFormatValues) => {
                // Read fresh data to avoid stale closure
                const freshData = $selectedProspectsData.get();
                const freshStoreData = freshData[row.original.limitedPartnerId];
                $selectedProspectsData.set({
                  ...freshData,
                  [row.original.limitedPartnerId]: {
                    ...freshStoreData,
                    estimatedTicketSizeMin:
                      values.floatValue !== undefined && currency
                        ? {
                            amount: values.floatValue,
                            currency,
                          }
                        : undefined,
                  },
                });
              }}
              tableRef={tableRef}
              value={storeData.estimatedTicketSizeMin?.amount || ""}
            />
          );
        },
      },
      {
        accessorKey: "estimatedTicketSizeMax",
        header: m.common_fieldOptional({
          name: m.coInvestments_prospects_estimatedTicketSizeMax(),
        }),
        size: 240,
        maxSize: 240,
        addCellTooltip: false,
        cell: ({ row }: CellContext<ProspectWithFormData, unknown>) => {
          const currentData = $selectedProspectsData.get();
          const storeData = currentData[row.original.limitedPartnerId];
          // Get errors from getter function to avoid stale closure
          const prospectErrors =
            getValidationErrors()[row.original.limitedPartnerId];
          const error = prospectErrors?.estimatedTicketSizeMax;

          return (
            <MoneyInputWithTooltipError
              allowAbbreviations
              currency={currency}
              error={error}
              hideLabelTooltip
              name={`estimatedTicketSizeMax-${row.original.limitedPartnerId}`}
              onValueChange={(values: NumberFormatValues) => {
                // Read fresh data to avoid stale closure
                const freshData = $selectedProspectsData.get();
                const freshStoreData = freshData[row.original.limitedPartnerId];
                $selectedProspectsData.set({
                  ...freshData,
                  [row.original.limitedPartnerId]: {
                    ...freshStoreData,
                    estimatedTicketSizeMax:
                      values.floatValue !== undefined && currency
                        ? {
                            amount: values.floatValue,
                            currency,
                          }
                        : undefined,
                  },
                });
              }}
              tableRef={tableRef}
              value={storeData.estimatedTicketSizeMax?.amount || ""}
            />
          );
        },
      },
      {
        accessorKey: "reasonForInclusion",
        header: m.common_fieldOptional({
          name: m.coInvestments_reasonForInclusion(),
        }),
        minSize: 250,
        addCellTooltip: false,
        cell: ({ row }: CellContext<ProspectWithFormData, unknown>) => {
          const currentData = $selectedProspectsData.get();
          const storeData = currentData[row.original.limitedPartnerId];
          const selectedReasons = reasonForInclusionOptions.filter((option) =>
            storeData.reasonForInclusion.includes(option.value),
          );
          const prospectErrors =
            getValidationErrors()[row.original.limitedPartnerId];
          const error = prospectErrors?.reasonForInclusion;

          return (
            <MultiselectWithTooltipError
              error={error}
              name={`reason-for-inclusion-${row.original.limitedPartnerId}`}
              onSetSelectedOptions={(options) => {
                // Read fresh data to avoid stale closure
                const freshData = $selectedProspectsData.get();
                const freshStoreData = freshData[row.original.limitedPartnerId];
                $selectedProspectsData.set({
                  ...freshData,
                  [row.original.limitedPartnerId]: {
                    ...freshStoreData,
                    reasonForInclusion: options.map((opt) => opt.value),
                  },
                });
              }}
              options={reasonForInclusionOptions}
              selectedOptions={selectedReasons}
              tableRef={tableRef}
            />
          );
        },
      },
      {
        accessorKey: "notes",
        header: m.common_fieldOptional({ name: m.coInvestments_notes() }),
        minSize: 420,
        addCellTooltip: false,
        cell: ({ row }: CellContext<ProspectWithFormData, unknown>) => (
          <div className="py-3">
            <TextareaInput
              defaultValue={row.original.notes || ""}
              name={`notes-${row.original.limitedPartnerId}`}
              onChange={(e) => {
                // Read fresh data to avoid stale closure
                const freshData = $selectedProspectsData.get();
                const freshStoreData = freshData[row.original.limitedPartnerId];
                $selectedProspectsData.set({
                  ...freshData,
                  [row.original.limitedPartnerId]: {
                    ...freshStoreData,
                    notes: e.target.value,
                  },
                });
              }}
              placeholder={m.coInvestments_notes()}
              rows={3}
            />
          </div>
        ),
      },
    ],
    [currency, getValidationErrors, tableRef, reasonForInclusionOptions],
  );

  return columns;
};
